import { useEffect, Suspense } from 'react';
import ThemeConfig from './theme';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  BrowserRouter
} from 'react-router';
import * as Sentry from '@sentry/react';
import GitInfo from 'react-git-info/macro';

import CONFIG from '@/config';
import { MainRouter } from './routes';
import LoadingScreen from './components/LoadingScreen';
import NotistackProvider from './components/NotistackProvider';
import { TerminalContextProvider } from './context/TerminalContext';
import { RuntimeInfoProvider } from './context/RuntimeInfo';
import LockScreen from './views/Auth/LockScreenModal/LockScreen';
import { LockProvider } from './context/LockContext';
import { checkFeatureFlag } from './common/utils/featureFlags';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter7Adapter } from './adapters/ReactRouter7Adapter';
import { DrawerProvider } from './context/DrawerContext';
import { QueryClientProvider } from './context/QueryClientProvider';
import { ShiftLeadAlertProvider } from './context/ShiftLeadAlertContext';

let env,
  envSentryErrorSampleRate,
  envSentryTracesSampleRate,
  envSentryEnableTracing,
  envSentryEnabled;
if (window.location.host.indexOf('support') !== -1) {
  env = 'support';
  envSentryEnabled = true;
  envSentryErrorSampleRate = checkFeatureFlag('SENTRY_ERROR_SAMPLING_RATE');
  envSentryEnableTracing = true;
  envSentryTracesSampleRate = checkFeatureFlag('SENTRY_TRACES_SAMPLING_RATE');
} else if (window.location.host.indexOf('staging') !== -1) {
  env = 'staging';
  envSentryEnabled = true;
  envSentryErrorSampleRate = checkFeatureFlag('SENTRY_ERROR_SAMPLING_RATE');
  envSentryEnableTracing = true;
  envSentryTracesSampleRate = checkFeatureFlag('SENTRY_TRACES_SAMPLING_RATE');
} else if (window.location.host.indexOf('training') !== -1) {
  env = 'training';
  envSentryEnabled = false;
  envSentryErrorSampleRate = checkFeatureFlag('SENTRY_ERROR_SAMPLING_RATE');
  envSentryEnableTracing = false;
  envSentryTracesSampleRate = checkFeatureFlag('SENTRY_TRACES_SAMPLING_RATE');
} else if (window.location.host.indexOf('test') !== -1) {
  env = 'test';
  envSentryEnabled = false;
  envSentryErrorSampleRate = checkFeatureFlag('SENTRY_ERROR_SAMPLING_RATE');
  envSentryEnableTracing = false;
  envSentryTracesSampleRate = checkFeatureFlag('SENTRY_TRACES_SAMPLING_RATE');
} else if (window.location.host.indexOf('pow.restore') !== -1) {
  env = 'prod';
  envSentryEnabled = true;
  envSentryErrorSampleRate = checkFeatureFlag('SENTRY_ERROR_SAMPLING_RATE');
  envSentryEnableTracing = true;
  envSentryTracesSampleRate = checkFeatureFlag('SENTRY_TRACES_SAMPLING_RATE');
} else if (window.location.host.indexOf('powdev') !== -1) {
  env = 'dev';
  envSentryEnabled = true;
  envSentryErrorSampleRate = checkFeatureFlag('SENTRY_ERROR_SAMPLING_RATE');
  envSentryEnableTracing = true;
  envSentryTracesSampleRate = checkFeatureFlag('SENTRY_TRACES_SAMPLING_RATE');
} else if (
  window.location.host.indexOf('localhost') !== -1 ||
  window.location.host.indexOf('127.0.0.1') !== -1
) {
  env = 'localhost';
  envSentryEnabled = false;
  envSentryErrorSampleRate = 0.0;
  envSentryEnableTracing = false;
  envSentryTracesSampleRate = 0.0;
} else {
  env = 'other';
  envSentryEnabled = false;
  envSentryErrorSampleRate = 0.0;
  envSentryEnableTracing = false;
  envSentryTracesSampleRate = 0.0;
}
const uiGitInfo = GitInfo();

Sentry.init({
  release: uiGitInfo.commit.shortHash,
  environment: env,
  enabled: envSentryEnabled,
  sampleRate:
    typeof envSentryErrorSampleRate === 'number'
      ? envSentryErrorSampleRate
      : undefined,
  enableTracing: envSentryEnableTracing,
  tracesSampleRate:
    typeof envSentryTracesSampleRate === 'number'
      ? envSentryTracesSampleRate
      : undefined,
  dsn: CONFIG.SENTRY_DSN,
  integrations(integrations) {
    const ourIntegrations = [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.browserApiErrorsIntegration({
        setTimeout: true,
        setInterval: true,
        requestAnimationFrame: true,
        XMLHttpRequest: true,
        eventTarget: true
      }),
      Sentry.captureConsoleIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.replayIntegration()
    ];
    // incase the breadcrumb integration causes any eventing issues, we can exclude it with:
    //return [ ...ourIntegrations, ...integrations.filter(integration => integration.name !== 'Breadcrumbs')];
    return [...ourIntegrations, ...integrations];
  }
});

function App() {
  return (
    <ThemeConfig>
      <RuntimeInfoProvider>
        <NotistackProvider>
          <QueryClientProvider>
            <TerminalContextProvider>
              <LockProvider>
                <ShiftLeadAlertProvider>
                  <Suspense fallback={<LoadingScreen />}>
                    <LockScreen />                  
                    <BrowserRouter>
                      <QueryParamProvider adapter={ReactRouter7Adapter}>
                        <DrawerProvider>
                          <MainRouter />
                        </DrawerProvider>
                      </QueryParamProvider>
                    </BrowserRouter>
                  </Suspense>
                </ShiftLeadAlertProvider>
              </LockProvider>
            </TerminalContextProvider>
          </QueryClientProvider>
        </NotistackProvider>
      </RuntimeInfoProvider>
    </ThemeConfig>
  );
}

export default App;
